import {
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  NgZone,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core'
import {faCamera, faCheck, faRotateLeft, faXmark} from '@fortawesome/free-solid-svg-icons'
import {environment} from '../../../../../../environments/environment'
import {
  aGrises,
  ANCHO_ANALISIS,
  brilloMedio,
  CALIDAD_JPG,
  ESPERA_MANUAL_MS,
  evaluarMuestra,
  IMuestraEnfoque,
  instruccionDeGuia,
  INTERVALO_ANALISIS_MS,
  IRect,
  LecturaEnfoque,
  motivoSinCamara,
  nombreDeFoto,
  RecuadroCamara,
  recorteDelMarco,
  RESTRICCIONES_CAMARA,
  seguimientoInicial,
  tamanoDeSalida,
  TEXTO_LECTURA,
  varianzaLaplaciano
} from '../../data/constants/camara-guia'

export type EstadoCamara = 'abriendo' | 'lista' | 'revisando' | 'sin-camara'

/** Circunferencia del anillo de avance del disparador (r = 37 en un viewBox de 80). */
const CIRCUNFERENCIA_ANILLO = 2 * Math.PI * 37

/**
 * Cámara de la liga con recuadro (ver data/constants/camara-guia.ts). Pantalla
 * completa: se mueve a <body> para que ningún ancestro con transform le rompa el
 * position: fixed. Estilos en src/assets/styles/components/_camara-guia.scss
 * (el build corta en 4 kB por componente).
 *
 * La foto se toma sola cuando lo del recuadro está nítido y quieto; si en
 * ESPERA_MANUAL_MS no lo logra, el disparador se habilita para tomarla a mano.
 * El análisis corre fuera de la zona de Angular (no dispara detección de cambios
 * en cada muestra): solo entra cuando cambia lo que se ve.
 *
 * Sale por `capturar` con la foto ya recortada (un File como el de un input),
 * por `cerrar`, o por `usarCamaraDelEquipo` si aquí no se pudo abrir la cámara:
 * el padre abre entonces el input de siempre en el mismo clic (sin perder el gesto).
 */
@Component({
  selector: 'app-camara-guia',
  templateUrl: './camara-guia.component.html'
})
export class CamaraGuiaComponent implements OnInit, OnDestroy {
  /** TARJETA / HOJA (Omnicanal) o PASAPORTE (identificación elegida). */
  @Input() guia: RecuadroCamara = 'TARJETA'
  /** «INE». */
  @Input() titulo = ''
  /** «Anverso» en los pasos de varios archivos; vacío en los de uno. */
  @Input() etiqueta = ''
  @Input() tipo: 'image/jpeg' | 'image/png' = 'image/jpeg'
  @Output() capturar = new EventEmitter<File>()
  @Output() cerrar = new EventEmitter<void>()
  @Output() usarCamaraDelEquipo = new EventEmitter<void>()

  @ViewChild('video', {static: true}) videoRef!: ElementRef<HTMLVideoElement>
  @ViewChild('marco', {static: true}) marcoRef!: ElementRef<HTMLElement>
  @ViewChild('botonCerrar', {static: true}) botonCerrarRef!: ElementRef<HTMLButtonElement>

  estado: EstadoCamara = 'abriendo'
  motivo = ''
  vistaPrevia: string | null = null
  procesando = false
  texto = instruccionDeGuia('TARJETA')

  // Foto automática: cómo va el enfoque y si ya se puede tomar a mano.
  lectura: LecturaEnfoque = 'enfocando'
  progresoAuto = 0
  manualDisponible = false
  destello = false
  /** Aviso en la revisión si se tomó a mano sin enfoque (o con poca luz). */
  avisoRevision = ''
  /** Números del análisis, solo con environment.camaraDebug (calibración en un celular real). */
  depuracion: {nitidez: number; maximo: number; movimiento: number | null; brillo: number} | null = null
  readonly circunferencia = CIRCUNFERENCIA_ANILLO
  protected readonly textoLectura = TEXTO_LECTURA
  protected readonly iconos = {cerrar: faXmark, repetir: faRotateLeft, usar: faCheck, camara: faCamera}

  private stream: MediaStream | null = null
  private foto: File | null = null
  private destruido = false
  private focoPrevio: HTMLElement | null = null
  private analisis: ReturnType<typeof setInterval> | null = null
  private esperaManual: ReturnType<typeof setTimeout> | null = null
  private lienzoAnalisis: HTMLCanvasElement | null = null
  private seguimiento = seguimientoInicial()
  private inicioLista = 0
  private readonly conDepuracion = !!(environment as {camaraDebug?: boolean}).camaraDebug

  constructor(private host: ElementRef<HTMLElement>, private zona: NgZone) {}

  ngOnInit() {
    this.texto = instruccionDeGuia(this.guia)
    this.focoPrevio = document.activeElement as HTMLElement | null
    document.body.appendChild(this.host.nativeElement)
    document.body.classList.add('cg-abierta')
    this.botonCerrarRef.nativeElement.focus({preventScroll: true})
    void this.abrir()
  }

  ngOnDestroy() {
    this.destruido = true
    this.detenerAnalisis()
    this.detener()
    this.soltarVistaPrevia()
    document.body.classList.remove('cg-abierta')
    this.host.nativeElement.remove()
    if (this.focoPrevio?.isConnected) this.focoPrevio.focus({preventScroll: true})
  }

  get tituloCompleto(): string {
    return [this.titulo, this.etiqueta].filter(Boolean).join(' · ')
  }

  async abrir() {
    this.estado = 'abriendo'
    this.motivo = ''
    const medios = typeof navigator !== 'undefined' ? navigator.mediaDevices : undefined
    // Sin HTTPS (o en un WebView sin permiso de cámara) no existe getUserMedia.
    if (!medios?.getUserMedia) {
      this.sinCamara('Este navegador no deja abrir la cámara desde aquí.')
      return
    }
    try {
      const stream = await medios.getUserMedia(RESTRICCIONES_CAMARA)
      if (this.destruido) {
        stream.getTracks().forEach((t) => t.stop())
        return
      }
      this.stream = stream
      this.pedirEnfoqueContinuo(stream)
      const video = this.videoRef.nativeElement
      video.srcObject = stream
      // iOS: con muted + playsinline arranca sin gesto; si no, el primer toque lo hace.
      await video.play().catch(() => undefined)
      if (this.destruido) return
      this.estado = 'lista'
      this.iniciarAnalisis()
    } catch (error) {
      if (!this.destruido) this.sinCamara(motivoSinCamara(error))
    }
  }

  // ---------- Foto automática ----------

  private iniciarAnalisis() {
    this.detenerAnalisis()
    this.seguimiento = seguimientoInicial()
    this.lectura = 'enfocando'
    this.progresoAuto = 0
    this.manualDisponible = false
    this.inicioLista = Date.now()
    this.esperaManual = setTimeout(() => (this.manualDisponible = true), ESPERA_MANUAL_MS)
    this.zona.runOutsideAngular(() => {
      this.analisis = setInterval(() => this.analizar(), INTERVALO_ANALISIS_MS)
    })
  }

  private detenerAnalisis() {
    if (this.analisis) clearInterval(this.analisis)
    if (this.esperaManual) clearTimeout(this.esperaManual)
    this.analisis = null
    this.esperaManual = null
  }

  /** Una muestra: se mide y, si cambió lo que se ve (o ya toca disparar), entra a Angular. */
  private analizar() {
    if (this.estado !== 'lista' || this.procesando || this.destruido) return
    const muestra = this.muestrear()
    if (!muestra) return
    const r = evaluarMuestra(this.seguimiento, muestra, Date.now() - this.inicioLista)
    this.seguimiento = r.seguimiento
    const cambio = r.lectura !== this.lectura || r.progreso !== this.progresoAuto || this.conDepuracion
    if (!cambio) return
    this.zona.run(() => {
      this.lectura = r.lectura
      this.progresoAuto = r.progreso
      if (this.conDepuracion) {
        this.depuracion = {nitidez: muestra.nitidez, maximo: r.seguimiento.maximo, movimiento: r.movimiento, brillo: muestra.brillo}
      }
      if (r.lectura === 'lista') this.tomarFoto('auto')
    })
  }

  /** Lo de dentro del recuadro (sin margen), chico y en grises. */
  private muestrear(): IMuestraEnfoque | null {
    const video = this.videoRef.nativeElement
    const recorte = recorteDelMarco({ancho: video.videoWidth, alto: video.videoHeight}, caja(video), caja(this.marcoRef.nativeElement), 0)
    if (!recorte) return null
    const ancho = Math.max(3, Math.min(ANCHO_ANALISIS, recorte.ancho))
    const alto = Math.max(3, Math.round(recorte.alto * (ancho / recorte.ancho)))
    const lienzo = (this.lienzoAnalisis ??= document.createElement('canvas'))
    if (lienzo.width !== ancho || lienzo.height !== alto) {
      lienzo.width = ancho
      lienzo.height = alto
    }
    const contexto = lienzo.getContext('2d', {willReadFrequently: true})
    if (!contexto) return null
    contexto.drawImage(video, recorte.x, recorte.y, recorte.ancho, recorte.alto, 0, 0, ancho, alto)
    const gris = aGrises(contexto.getImageData(0, 0, ancho, alto).data)
    return {nitidez: varianzaLaplaciano(gris, ancho, alto), brillo: brilloMedio(gris), gris}
  }

  // Donde el navegador lo deja (Chrome en Android), enfoque continuo de la cámara.
  private pedirEnfoqueContinuo(stream: MediaStream) {
    const pista = stream.getVideoTracks?.()[0]
    const capacidades = pista?.getCapabilities?.() as {focusMode?: string[]} | undefined
    if (pista && capacidades?.focusMode?.includes('continuous')) {
      pista.applyConstraints({advanced: [{focusMode: 'continuous'} as MediaTrackConstraintSet]}).catch(() => undefined)
    }
  }

  // ---------- Tomar, revisar, entregar ----------

  /** A mano solo cuando ya pasó la espera (antes la toma sola al enfocar). */
  tomarFoto(origen: 'auto' | 'manual' = 'manual') {
    if (this.estado !== 'lista' || this.procesando) return
    if (origen === 'manual' && !this.manualDisponible) return
    const video = this.videoRef.nativeElement
    const recorte = recorteDelMarco(
      {ancho: video.videoWidth, alto: video.videoHeight},
      caja(video),
      caja(this.marcoRef.nativeElement)
    )
    if (!recorte) return
    const salida = tamanoDeSalida(recorte)
    const lienzo = document.createElement('canvas')
    lienzo.width = salida.ancho
    lienzo.height = salida.alto
    const contexto = lienzo.getContext('2d')
    if (!contexto) {
      this.sinCamara('No pudimos procesar la foto en este equipo.')
      return
    }
    contexto.drawImage(video, recorte.x, recorte.y, recorte.ancho, recorte.alto, 0, 0, salida.ancho, salida.alto)
    this.detenerAnalisis()
    this.avisoRevision = origen === 'auto' ? '' : avisoDeTomaManual(this.lectura)
    if (origen === 'auto') this.avisarDisparo()
    this.procesando = true
    lienzo.toBlob((blob) => this.zona.run(() => {
      this.procesando = false
      if (this.destruido) return
      if (!blob) {
        this.sinCamara('No pudimos procesar la foto en este equipo.')
        return
      }
      this.soltarVistaPrevia()
      this.foto = new File([blob], nombreDeFoto(this.titulo, this.etiqueta, this.tipo), {type: this.tipo, lastModified: Date.now()})
      this.vistaPrevia = URL.createObjectURL(blob)
      this.estado = 'revisando'
    }), this.tipo, CALIDAD_JPG)
  }

  repetir() {
    this.soltarVistaPrevia()
    this.foto = null
    this.avisoRevision = ''
    if (this.stream) {
      this.estado = 'lista'
      this.iniciarAnalisis()
    } else {
      void this.abrir()
    }
  }

  usar() {
    if (this.foto) this.capturar.emit(this.foto)
  }

  cancelar() {
    this.cerrar.emit()
  }

  sinRecuadro() {
    this.usarCamaraDelEquipo.emit()
  }

  @HostListener('document:keydown.escape')
  alEscape() {
    if (this.estado === 'revisando') this.repetir()
    else this.cancelar()
  }

  // Se tomó sola: destello y vibración corta (donde la hay) para que se note.
  private avisarDisparo() {
    this.destello = true
    setTimeout(() => (this.destello = false), 280)
    try {
      navigator.vibrate?.(40)
    } catch {
      // Sin vibración (iOS, escritorio): no pasa nada.
    }
  }

  private sinCamara(motivo: string) {
    this.detenerAnalisis()
    this.detener()
    this.motivo = motivo
    this.estado = 'sin-camara'
  }

  private detener() {
    this.stream?.getTracks().forEach((t) => t.stop())
    this.stream = null
    const video = this.videoRef?.nativeElement
    if (video) video.srcObject = null
  }

  private soltarVistaPrevia() {
    if (this.vistaPrevia) URL.revokeObjectURL(this.vistaPrevia)
    this.vistaPrevia = null
  }
}

function caja(elemento: Element): IRect {
  const r = elemento.getBoundingClientRect()
  return {x: r.left, y: r.top, ancho: r.width, alto: r.height}
}

/** Tomada a mano: qué tan bien venía la imagen en ese momento. */
function avisoDeTomaManual(lectura: LecturaEnfoque): string {
  if (lectura === 'oscuro') return 'Salió con poca luz. Si no se lee bien, repítela en un lugar más iluminado.'
  if (lectura === 'moviendo' || lectura === 'enfocando') return 'Puede verse borrosa. Si no se lee bien, repítela.'
  return ''
}
